import _ from "lodash";
import { v4 as uuidv4 } from 'uuid';

import {buildQuestionUIDForMember, Service} from "./service";
import {buildDataSteps} from "./mock_kundendaten"
import {BankSettingsHandlerResource, QuestionnairesHandlerResource} from "../../utils/api";
import {
  buildCustomerDataForUpdate,
  getDanteCustomerData,
  getDanteCustomerFullName,
  getLegitimationData,
  getLegitimationFileFormDataFromGuid,
  getMemberFieldName,
  getQuestionType,
  getUID,
  setQuestionAnswerFromConfig,
  UpdateCustomerAndSync,
  UpdateCustomerData
} from "./utils";
import {
  buildStepUIDForMember, CountryISOMapping,
  COUPLE_MEMBERS_CONFIRMATION_STEP_IDENTIFIERS,
  COUPLE_MEMBERS_OTHER_FIELDS, DefaultCountry, EMPLOYER_STEP_ID,
  LEGITIMATION_DOCUMENTS_STEP_IDENTIFIERS, LEGITIMATION_MAPPING,
  QUESTION_TYPE, REF_ACCOUNT_STEP_ID, TAX_STEP_IDENTIFIERS,
  USER_CONFIRMATION_STEP_IDENTIFIER
} from "./constants";
import {getErrorMessage} from "../../utils/utils";
import {SERVICE_CONCEPTS} from "../Trades/constants";
import {getMemberIndexFromStepUID} from "./services";
import {bankAccountToOption} from "./components/StepContent/components/question/ReferenceAccountsQuestion/ReferenceAccountsQuestion";
import {SERVER_ERROR} from "../../utils/constants";
import {
  countriesChoices
} from "./components/StepContent/components/question/CountrySelectQuestion/CountrySelectQuestion";

const getMainBankAccount = (bankAccounts) => {
  if (!bankAccounts) return undefined

  return _.find(bankAccounts, account => account.mainBankAccount)
}

const MAX_TAX_ID_COUNT = 2;

export class CustomerService extends Service {

  async updateCustomerMemberData() {
    const memberIndex = getMemberIndexFromStepUID(getUID(this._step.uid))
    if (!_.isUndefined(memberIndex) && this.members[memberIndex]) {
      let customerData = await UpdateCustomerAndSync(this.members[memberIndex].id, this._step, undefined, undefined, this.isSwitchSellFlow);

      if (!COUPLE_MEMBERS_CONFIRMATION_STEP_IDENTIFIERS.includes(this._step.uid)) return

      if (customerData) {
        let userConfirmationStep = _.find(this._questionnaire.steps, (step) => step.uid === USER_CONFIRMATION_STEP_IDENTIFIER)
        if (!!userConfirmationStep) {
          let { data } = customerData
          this.members[memberIndex] = data

          let updatedData = {
            "relationships": this.members
          }

          this._questionnaire.steps.map((step) => {

            if (step.withUserName && getMemberIndexFromStepUID(step.uid) == getMemberIndexFromStepUID(this.step.uid)) {
              this.__updateStepNameWithCustomer(step, getDanteCustomerFullName(data));
            }

            (step.question || []).map((q) => {
              if (getUID(step.uid) === USER_CONFIRMATION_STEP_IDENTIFIER) {
                setQuestionAnswerFromConfig(q, updatedData);
              }
            })
          })

          await super.__sendStepData(false, userConfirmationStep)

          // await this.__sendStepAnswerData(userConfirmationStep)
        }
      }

    }
  }

  getExtraDataForLegitimation(memberIndex, customerType){
    const extraInfo = {};
    _.set(extraInfo, 'legitimation.0.economicBeneficiary',
      this.getStepAnswer(
        buildStepUIDForMember('A1', memberIndex),
        buildQuestionUIDForMember('Berechtigter', memberIndex, customerType)));
    _.set(extraInfo, 'legitimation.0.peP',
      this.getStepAnswer(
        buildStepUIDForMember('A1', memberIndex),
        buildQuestionUIDForMember('pep', memberIndex, customerType)));
    _.set(extraInfo, 'legitimation.0.fatca',
      this.getStepAnswer(
        buildStepUIDForMember('taxable', memberIndex),
        buildQuestionUIDForMember('usa_steuerpflichtig', memberIndex, customerType)));

    // All legitimations, that confirmed in investment app
    // should be used as main Legitimation document
    _.set(extraInfo, 'legitimation.0.main', true)

    return extraInfo;
  }

  _handleLegitimationGUID(step, memberIndex, customerType, name='legitimation'){
    //region Functionality to handle GUID in correct way,
    // if type and number already exists in crm - legitimation should be updated.
    // Otherwise - new legitimation should be created
    let legitimationTypeQuestion = this.findQuestionDeep(
      step, buildQuestionUIDForMember(`${name}['type']`, memberIndex, customerType));
    let legitimationNumber = this.getStepAnswer(step.uid, buildQuestionUIDForMember(`${name}['number']`, memberIndex, customerType));
    const legitimationType = legitimationTypeQuestion.answer;

    const legitimationData = getLegitimationData(step.all_legitimations || [], legitimationType, legitimationNumber, legitimationTypeQuestion.config.mapping.config);
    const legitimationGUID = this.findQuestionDeep(step, buildQuestionUIDForMember(`${name}['GUID']`, memberIndex, customerType));
    // if legitimation with type + number already exists - update it, otherwise - create new
    legitimationGUID.answer = legitimationData ? legitimationData.GUID : null;
    //endregion

    let uploadRegCert;
    if(legitimationType === LEGITIMATION_MAPPING.Reisepass){
      uploadRegCert = this._handleLegitimationGUID(step, memberIndex, customerType, 'registration_certificate');
    }

    const self = this;

    async function uploadFiles(customerToUpdate, customerUpdate){
      if(customerUpdate) {
        // Update legitimation GUID (in case it was created)
        if (!legitimationGUID.answer) {
          // find by identificationNumber in case there are other entities in CRM
          const legData = getLegitimationData(step.all_legitimations, legitimationType, legitimationNumber, legitimationTypeQuestion.config.mapping.config);
          legitimationGUID.answer = legData.GUID;
        }
      }

      const legitimationFileData = _.get(legitimationData, 'identificationDocument');
      await self._sendLegitimationDocument(customerToUpdate,
        self.findQuestionDeep(step, buildQuestionUIDForMember(`${name}['front_side']`, memberIndex, customerType)),
        legitimationGUID.answer, legitimationType, false, legitimationFileData
      );

      await self._sendLegitimationDocument(customerToUpdate,
        self.findQuestionDeep(step, buildQuestionUIDForMember(`${name}['rear_side']`, memberIndex, customerType)),
        legitimationGUID.answer, legitimationType, true, legitimationFileData
      );

      if(uploadRegCert){
        await uploadRegCert(customerToUpdate, customerUpdate)
      }
    }

    return uploadFiles;
  }

  /**
   * Send legitimation data from step.
   * @param {*} step - Step data
   * @param customerType
   * */
  async _sendUserLegitimationStepData(step, customerType) {
    if (this.current_customer.read_only) return;

    const memberIndex = getMemberIndexFromStepUID(step.uid);

    const skipLegitimationSynchronization = this.getStepAnswer(
      step.uid, buildQuestionUIDForMember("legitimation_is_valid", memberIndex, customerType)) || false;

    if (!this.is_trading && !this.isNewDesign || !skipLegitimationSynchronization) {
      const extraInfo = this.getExtraDataForLegitimation(memberIndex, customerType);

      const uploadFiles = this._handleLegitimationGUID(step, memberIndex, customerType);

      // Step 1: update legitimation info if changed
      let customerToUpdate = (!_.isUndefined(memberIndex) && this.members[memberIndex]) ? this.members[memberIndex].id : this._customer_id;
      const customerUpdate = await UpdateCustomerData(customerToUpdate, step, undefined, extraInfo);

      if(customerUpdate) {
        // Update list of all legitimations in case new legitimation was added
        step.all_legitimations = customerUpdate.data.legitimation;
      }

      // Step 2: upload new files
      await uploadFiles(customerToUpdate, customerUpdate);

      // store defaultAnswersJSON after upload documents
      step.defaultAnswersJSON = JSON.stringify(buildCustomerDataForUpdate(step,undefined, extraInfo));
    }

  }

  async __sendStepData() {
    if(LEGITIMATION_DOCUMENTS_STEP_IDENTIFIERS.includes(this._step.uid)){
      await this._sendUserLegitimationStepData(this._step, this.customer_type)
    } else {
      const shouldUpdateMembers = this.useMembers && Array.isArray(this.members) && this.members.length
      if(this._step.uid === USER_CONFIRMATION_STEP_IDENTIFIER){
        await UpdateCustomerAndSync(this._customer_id, this._step, undefined, undefined, this.isSwitchSellFlow);
      } else if (shouldUpdateMembers && COUPLE_MEMBERS_OTHER_FIELDS.includes(getUID(this._step.uid))) {
        await this.updateCustomerMemberData()
      } else {
        await UpdateCustomerData(this._customer_id, this._step);
      }
    }

    await this._validateAccount();

    return await super.__sendStepData(); // store answers in our db
  }

  _isLegitimationGUIDMissing = (currGUID, legitimationFileData, backSide) => {
    const GUID = _.get(legitimationFileData, [backSide ? 'LegitimationBack' : 'Legitimation']);
    return !GUID || currGUID.toUpperCase() !== GUID.toUpperCase();
  };

  async _sendLegitimationDocument(customerId, question, legitimationGUID, legitimationType, backside, legitimationFileData){
    // we upload file in two cases: 1st - new formData (new file), 2nd - file GUID is missing (new legitimation was created)
    let {formData, guid} = question.answer || {};

    if (!formData && !!guid && this._isLegitimationGUIDMissing(guid, legitimationFileData, backside)){
      // use fileFormData (downloaded for preview) to skip unnecessary file retrival
      formData = question.fileFormData || getLegitimationFileFormDataFromGuid(guid)
    }

    // if Legitimation has formData (file) - send it
    if (formData){
      formData.append('backside', String(!!backside));
      formData.append('legitimation_type', legitimationType);

      try {
        const res = await QuestionnairesHandlerResource.at(
          `customer-legitimation-document/${customerId}/${legitimationGUID}/`).post(formData);
        // if file was sent update GUID and remove formData to prevent send it again
        question.answer = {guid: res.DokumentGUID};
      } catch (e) {
        const ERROR_MESSAGES_TRANSLATIONS = {
          413: "Die Ausweisdokumente sind zu gross.",
          502: SERVER_ERROR
        };

        const msg = _.get(ERROR_MESSAGES_TRANSLATIONS, e.status) || getErrorMessage(e.data || e);

        question.error = _.get(e, 'data.errors.file.0', msg);
        // get general error from any upload or use default
        // Dante: An error occurred while uploading the legitimation document. Please try to upload the document again.
        throw _.get(e, 'data.message') || 'Dante: Beim Hochladen des Legitimationsdokumentes ist ein Fehler aufgetreten. Bitte versuchen Sie das Dokument erneut hochzuladen.';
      }
    }
  }

  _buildSteps(customer){
    const isVlPlan = this._getPrevStepAnswer('products_selection', 'product-selection-deposits', "vl_plan['checkbox']");

    let originOfAssetsOptions = [];

    const productsService = this._getStepService('products_selection');
    const depot = productsService.getDepot(true);

    if(depot) {
      originOfAssetsOptions = _.get(productsService.banksDataGrouped, [depot.bankCompanyId, 'onboarding_source_of_funds_choices'])
    }

    return buildDataSteps(customer, isVlPlan, originOfAssetsOptions)
  }

  __updateStepNameWithCustomer = (step, newCustomerName) => {
    let stepName = step.name.split(' ')[0];
    step.name = `${stepName} - ${newCustomerName}`
  };

  async __setQuestionnaire(onboardingAnswers) {
    await super.__setQuestionnaire(onboardingAnswers);
    await this._getBankDetails();
    let customer = await getDanteCustomerData(this._customer_id);

    this.customer_type = customer.customer_type;
    this.members = customer.relationships;
    this.current_customer = customer;

    // override PEP from User Confirm / Risk Profile
    _.set(customer, 'legitimation.0.peP',this._getPrevStepAnswer(
      this.serviceConcept == SERVICE_CONCEPTS.Anlageberatung ? "risk_profile" : "user_confirmation", "A1", "A12"));

    const preparedQuestionnaire = this._buildSteps(customer);
    // for pure sell flow we skip legitimation step
    if(this.isSellOnlyFlow){
      preparedQuestionnaire.steps = preparedQuestionnaire.steps.filter(step => !LEGITIMATION_DOCUMENTS_STEP_IDENTIFIERS.includes(step.uid))
    }

    this._questionnaire = this.__deepCopy(preparedQuestionnaire);

    let hasRefAccountQ = false;

    this._questionnaire.steps.map((step) => {
      (step.question || []).map((q) => {
        q.uid !== 'steuerpflichtig' && this.__checkReadOnlyQuestion(q);

        if (COUPLE_MEMBERS_OTHER_FIELDS.includes(getUID(step.uid))) {
          const memberIndex = getMemberIndexFromStepUID(getUID(step.uid))

          if(!_.isUndefined(memberIndex) && this.members[memberIndex]) {
            setQuestionAnswerFromConfig(q, this.members[memberIndex]);
          }
        } else {
          setQuestionAnswerFromConfig(q, customer);
        }

        if([QUESTION_TYPE.REFERENCE_ACCOUNTS, QUESTION_TYPE.REFERENCE_ACCOUNTS_V2].includes(getQuestionType(q))){
          q.bank_accounts = _.get(customer, 'bankAccounts', []);
          let mainBankAccount = getMainBankAccount(q.bank_accounts || {});
          q.answer = mainBankAccount ? bankAccountToOption(mainBankAccount) : undefined;

          hasRefAccountQ = true;
        }
      });
      if(step.uid === REF_ACCOUNT_STEP_ID && !step.name){
        let stepName = this.is_trading ? "Referenzkonto/Verrechnungskonto<:placeholder> auswählen" : "Referenzkonto<:placeholder>"
        step.name = stepName.replace('<:placeholder>', this.is_couple ? " für die Gemeinschaft" : "")
      }
      // For Trading flow we have a list of banks, so please update get is_employer_information_mandatory flag if we add VL to Trading
      if(step.uid === EMPLOYER_STEP_ID){
        this._checkEmployerStepQuestions(step);
      }

      if (LEGITIMATION_DOCUMENTS_STEP_IDENTIFIERS.includes(getUID(step.uid))) {
        const memberIndex = getMemberIndexFromStepUID(getUID(step.uid))

        if(!_.isUndefined(memberIndex) && this.members[memberIndex]) {
          step.all_legitimations = this.members[memberIndex].all_legitimations
        } else {
          step.all_legitimations = customer.all_legitimations
        }
      }
    });

    if(hasRefAccountQ && this.isNewDesign){
      this.refAccountBanksLogos = await BankSettingsHandlerResource.at('ref-account-bank-logos/').get()
    }

    this._setDefaultAnswers(onboardingAnswers);

    this._questionnaire.steps.forEach((step) => {
      const isLegitimationStep = LEGITIMATION_DOCUMENTS_STEP_IDENTIFIERS.includes(step.uid);
      if(isLegitimationStep){
        this.__initRegCertificate(step);
      } else if(TAX_STEP_IDENTIFIERS.has(step.uid)){
        const memberIndex = getMemberIndexFromStepUID(step.uid);
        const steuerpflichtigQ = this.findQuestionDeep(step, buildQuestionUIDForMember("steuerpflichtig", memberIndex, this.customer_type));
        const taxIdentifiersQ = this.findQuestionDeep(step, buildQuestionUIDForMember("tax_identifiers", memberIndex, this.customer_type));
        if(!steuerpflichtigQ.answer) {
          steuerpflichtigQ.answer = steuerpflichtigQ.config.choices[0].uid;
          taxIdentifiersQ.answer = this.initTaxIdentifiers(taxIdentifiersQ, steuerpflichtigQ.answer, step.uid);
        } else if (_.isEmpty(taxIdentifiersQ.answer) || taxIdentifiersQ.answer.length > MAX_TAX_ID_COUNT) {
          taxIdentifiersQ.answer = this.initTaxIdentifiers(taxIdentifiersQ, steuerpflichtigQ.answer, step.uid);
        }
      }
      const extraData = isLegitimationStep
        ? this.getExtraDataForLegitimation(getMemberIndexFromStepUID(step.uid), this.customer_type)
        : {};
      step.defaultAnswersJSON = JSON.stringify(buildCustomerDataForUpdate(step, undefined, extraData));
    })
  }

  __initRegCertificate(step) {
    // hide regCertificate question if not reisepass
    const memberIndex = getMemberIndexFromStepUID(step.uid);

    const legitimationTypeQuestion = _.find(
      step.question,
      (q) => q.uid == buildQuestionUIDForMember("legitimation['type']", memberIndex, this.customer_type));
    const regCertQuestion = _.find(step.question, (q) => q.uid == "registration_certificate");

    regCertQuestion._question = regCertQuestion.question; // symlink, now we could set question = [] to ignore regCert

    if(legitimationTypeQuestion.answer !== LEGITIMATION_MAPPING.Reisepass){
      regCertQuestion.question = [];
    }
  }

  async confirmResult(data) {
    this._is_finished = true;
  }

  getBankConfig(){
    const productService = this._getStepService('products_selection');
    return productService && productService.getBankConfig();
  }

  getDataForAccount(validate) {
    let answers = this.getStepsAnswers(validate);
    if(answers.bank_account){
      answers.bank_account = {
        ...answers.bank_account,
        credit_institute_name: answers.bank_account.creditInstituteName,
        account_holder_full_name: answers.bank_account.account_holder
      }
    }
    // For Trading flow we have a list of banks, so please update get is_employer_information_mandatory flag if we add VL to Trading
    if(answers.employer) {
      if(!_.get(this.getBankConfig(), 'is_employer_information_mandatory')) {
        answers.employer = Object.entries(answers.employer).reduce((a,[k,v]) => (!v ? a : (a[k]=v, a)), {});
        if(_.isEmpty(answers.employer)) {
          delete answers.employer
        }
      }
    }

    const setTaxIdentifiers = (answers) => {
      if (answers.tax_identifiers && !_.isEmpty(answers.tax_identifiers)) {
        answers.tax_identifiers.forEach((taxIdItem, index) => {
          answers[`tax_id_${index + 1}`] = taxIdItem.tax_id;
          answers[`tax_country_${index + 1}`] = _.get(taxIdItem, 'country.id');
        })

        delete answers.tax_identifiers;
      }
    };

    // in case legitimation is "Reisepass" set "backside" from RegistrationCertificate
    const setRegCertificate = (answers) => {
      if (_.get(answers, 'legitimation.type') === LEGITIMATION_MAPPING.Reisepass) {
        const regCert = _.get(answers, 'registration_certificate.front_side');
        if (regCert) {
          _.set(answers.legitimation, 'rear_side', regCert)
        }
      }
    };

    setTaxIdentifiers(answers);
    setRegCertificate(answers);
    const memberFieldName = getMemberFieldName(this.customer_type);
    if (!_.isEmpty(answers[memberFieldName])) {
      answers[memberFieldName].forEach((holder) => {
        setTaxIdentifiers(holder);
        setRegCertificate(holder);
      });
    }

    return answers;
  }

  _getCustomerTaxInformation(stepUID){
    if (!this.current_customer) {
      return [];
    }

    const memberIndex = getMemberIndexFromStepUID(getUID(stepUID || this.step.uid))
    if(!_.isUndefined(memberIndex) && this.members[memberIndex]) {
      return this.members[memberIndex].taxInformation || [];
    }

    return this.current_customer.taxInformation || []
  }

  initTaxIdentifiers(taxIdentifiersQ, steuerpflichtigAnswer, currentStepUID){
    taxIdentifiersQ.error = null;
    const customerTaxInformation = this._getCustomerTaxInformation(currentStepUID);

    let taxIdentifiersAnswer = taxIdentifiersQ.answer || [];

    // Insert first option as Deutchland only in case first option is other that Deutchland
    if (['1', '2'].includes(steuerpflichtigAnswer) && _.get(taxIdentifiersAnswer, '0.country.id') != DefaultCountry) {
      const defaultCountry = countriesChoices.find((country) => country.id === DefaultCountry);
      const germanTaxIdentifier = _.find(customerTaxInformation, (item) => item.countryISO == 'DEU');
      taxIdentifiersAnswer.unshift({
        country: defaultCountry,
        id: uuidv4(),
        tax_id: _.get(germanTaxIdentifier, 'taxId') || '',
      });
    }

    if (steuerpflichtigAnswer === '1') {
      taxIdentifiersAnswer = [taxIdentifiersAnswer[0]];
    }

    //Prefill other options with other tax id if it exists (data from CRM);
    if (['2', '3'].includes(steuerpflichtigAnswer)) {
      const otherTaxIdentifiers = _.filter(customerTaxInformation, (item) => item.countryISO != 'DEU');
      if (otherTaxIdentifiers.length) {
        otherTaxIdentifiers.forEach((taxIdentifier) => {

          // In case answer already has tax id with country - do nothing;
          if (!!_.find(taxIdentifiersAnswer, (answerTaxId) => _.get(answerTaxId, 'country.id') == CountryISOMapping[taxIdentifier.countryISO])) {
            return
          }

          taxIdentifiersAnswer.push({
            id: uuidv4(),
            tax_id: taxIdentifier.taxId,
            country: countriesChoices.find((country) => country.id === CountryISOMapping[taxIdentifier.countryISO])
          });
        })
      }
    }

    // For 1 and 2 options first country is always Deutchland
    // and it is not possible to change country
    _.set(taxIdentifiersAnswer, '0.country_disabled', steuerpflichtigAnswer !== '3');

    if (taxIdentifiersAnswer.length > MAX_TAX_ID_COUNT) {
      taxIdentifiersAnswer.splice(MAX_TAX_ID_COUNT);
    }

    return taxIdentifiersAnswer;
  }
}